import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';

import {
  Category,
  Category_BigCategory,
  Category_BigCategory_MediumCategory,
  GetCategoryRequest,
} from '@proto/store/api/store/category';
import { BaseViewModel } from '../../base-view-model';
import { WindowDetectService } from '@service/window-detect.service';
import { CategoryRepositoryService } from '@service/category-repository.service';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { Constants } from '@helper/constants';
import { Links } from '@helper/links';
import { MetaData } from '@helper/meta-data';
import { GetSalesEventV2Request, GetSalesEventV2Request_Mode, SalesEvent_Event } from '@proto/store/api/store/home';
import { SalesEventRepositoryService } from '@service/sales-event-repository.service';

import { isEqual } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class MarketingCategoryModel extends BaseViewModel<Category> {
  get isPreview(): boolean {
    return this.router.url.includes(RoutePaths.PREVIEW);
  }

  readonly currentCategory = computed(
    () => {
      return (
        this.apiResult().bigCategories.find(c => c.nameEn === this.currentBigId()) ??
        Category_BigCategory.fromPartial({})
      );
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly bigCategoryName = computed(() => {
    const category = this.currentCategory();
    if (category) {
      return Links.getSiteLocale() === 'en' ? `${category.nameEn} ` : category.name;
    }
    return '';
  });
  // only used for mobile
  readonly currentMedium = computed(
    () => {
      return (
        this.currentCategory().mediumCategories.find(c => c.nameEn === this.currentMediumId()) ??
        this.currentCategory().mediumCategories[0] ??
        Category_BigCategory_MediumCategory.fromPartial({})
      );
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly mediumId = computed(() =>
    this.windowDetectService.isMobileDevice()
      ? this.currentMedium().nameEn || this.currentMediumId()
      : this.currentMediumId()
  );
  readonly currentBigId = signal('');
  readonly currentMediumId = signal('');
  readonly currentSmallId = signal('');
  readonly scrollPosition = signal(0);
  readonly canScroll = computed(() => this.scrollPosition() !== 0);
  readonly events = signal([] as SalesEvent_Event[]);
  readonly styleSelected = computed(() => this.windowDetectService.currentPath().includes(RoutePaths.OUTFIT));

  private readonly location = inject(Location);
  private readonly windowDetectService = inject(WindowDetectService);

  constructor(
    private lativRepository: CategoryRepositoryService,
    private salesEventRepository: SalesEventRepositoryService
  ) {
    super(Category.fromPartial({}));
    effect(() => {
      if (
        this.currentCategory().nameEn &&
        !this.currentCategory().mediumCategories.some(m =>
          m.smallCategories.some(s => s.nameEn === this.currentSmallId())
        )
      ) {
        this.currentSmallId.set('');
      }
      if (!this.currentCategory().nameEn) {
        this.currentBigId.set('');
      }
    });
    if (!this.windowDetectService.isMobileDevice()) {
      effect(() => {
        // reset categories selections when page change
        const path = this.windowDetectService.currentPath();
        if (
          !path.includes(RoutePaths.HOME) &&
          !path.includes(RoutePaths.PRODUCT) &&
          !path.includes(RoutePaths.CATEGORY)
        ) {
          this.resetCategories();
        }
      });
    }
  }

  getSalesEvent(ref: DestroyRef): void {
    this.salesEventRepository
      .getSalesEventV2(GetSalesEventV2Request.fromPartial({ mode: GetSalesEventV2Request_Mode.MODE_META }))
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: res => this.events.set(res.events),
        error: err => this.setApiError(err),
      });
  }

  getCategories(ref: DestroyRef, bigNameEn?: string, mediumNameEn?: string): void {
    this.lativRepository
      .getCategory(
        GetCategoryRequest.fromPartial({
          preview: this.isPreview,
        })
      )
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: category => {
          this.setApiResult(category);
          if (this.windowDetectService.isMobileDevice() && this.location.path().includes(RoutePaths.CATEGORY)) {
            const big = category.bigCategories.find(b => b.nameEn === bigNameEn);
            if (big) {
              this.currentBigId.set(big.nameEn);
              const medium = big.mediumCategories.find(m => m.nameEn === mediumNameEn);
              this.currentMediumId.set(medium?.nameEn ?? big.mediumCategories?.[0]?.nameEn ?? '');
            } else {
              this.currentBigId.set(category.bigCategories?.[0]?.nameEn ?? '');
              this.currentMediumId.set(category.bigCategories?.[0]?.mediumCategories?.[0]?.nameEn ?? '');
            }
          }
        },
        error: err => this.setApiError(err),
      });
  }

  goHome(): void {
    this.resetCategories();
    this.router.navigate([RouteHelper.home()]);
  }

  resetCategories(): void {
    this.currentBigId.set('');
    this.currentMediumId.set('');
    this.currentSmallId.set('');
  }

  override getMetaByUrl(segments: string[], model: Category): MetaData | undefined {
    if (segments[0] !== RoutePaths.CATEGORY) {
      return undefined;
    }
    const categories: string[] = [];
    if (segments[1]) {
      categories.push(segments[1]);
    }
    if (segments[2]) {
      const medium =
        model.bigCategories.find(b => b.nameEn === segments[1])?.mediumCategories?.find(m => m.nameEn === segments[2])
          ?.name ?? '';
      if (medium) {
        categories.push(medium);
      }
    }
    if (segments[3]) {
      const small =
        model.bigCategories
          .find(b => b.nameEn === segments[1])
          ?.mediumCategories?.find(m => m.nameEn === segments[2])
          ?.smallCategories.find(s => s.nameEn === segments[3])?.name ?? '';
      if (small) {
        categories.push(small);
      }
    }
    return new MetaData(categories.join('-') || Constants.PAGE_TITLE);
  }
}
